// Per-project automation rules cache. Loaded when a project opens and refreshed
// after any mutation. Mirrors the structure of store/workItemTypes.ts.

import { signal, type Signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import type { ApiAutomationRule } from "@juuplan/shared";
import {
  listAutomationRules,
  createAutomationRule as apiCreateAutomationRule,
  updateAutomationRule as apiUpdateAutomationRule,
  deleteAutomationRule as apiDeleteAutomationRule,
  type AutomationRuleCreate,
  type AutomationRuleUpdate,
} from "../lib/api";

export interface AutomationRulesState {
  loading: boolean;
  items: ApiAutomationRule[];
  projectId: number | null;
  error: string | null;
}

const initial: AutomationRulesState = {
  loading: true,
  items: [],
  projectId: null,
  error: null,
};

const automationRulesSignal: Signal<AutomationRulesState> = signal(initial);
let inFlight: Promise<void> | null = null;

async function loadAutomationRules(projectId: number): Promise<void> {
  try {
    const items = await listAutomationRules(projectId);
    automationRulesSignal.value = { loading: false, items, projectId, error: null };
  } catch (err) {
    const message = err instanceof Error ? err.message : "Unknown error";
    automationRulesSignal.value = {
      loading: false,
      items: [],
      projectId,
      error: message,
    };
  } finally {
    inFlight = null;
  }
}

/**
 * Hook: subscribe to the automation rules for the given project.
 * Triggers a load when the projectId changes, and guards against returning
 * a prior project's data while the new load is still in flight.
 */
export function useAutomationRules(projectId: number): AutomationRulesState {
  useEffect(() => {
    const s = automationRulesSignal.value;
    if (!inFlight && (s.projectId !== projectId || s.error !== null)) {
      automationRulesSignal.value = {
        loading: true,
        items: [],
        projectId,
        error: null,
      };
      inFlight = loadAutomationRules(projectId);
    }
  }, [projectId]);

  const state = automationRulesSignal.value;
  if (state.projectId !== projectId) {
    return { loading: true, items: [], projectId, error: null };
  }
  return state;
}

/** Force a fresh fetch — use after any schema-changing mutation. */
export function refreshAutomationRules(projectId: number): Promise<void> {
  automationRulesSignal.value = { ...automationRulesSignal.value, loading: true };
  inFlight = loadAutomationRules(projectId);
  return inFlight;
}

// ---------- CRUD helpers ----------

/** Create a new automation rule and refresh the cache. */
export async function addAutomationRule(
  projectId: number,
  body: AutomationRuleCreate
): Promise<ApiAutomationRule> {
  const created = await apiCreateAutomationRule(projectId, body);
  await refreshAutomationRules(projectId);
  return created;
}

/** Update an automation rule and refresh the cache. */
export async function patchAutomationRule(
  ruleId: number,
  projectId: number,
  body: AutomationRuleUpdate
): Promise<ApiAutomationRule> {
  const updated = await apiUpdateAutomationRule(ruleId, body);
  await refreshAutomationRules(projectId);
  return updated;
}

/** Delete an automation rule and remove it from the cache. */
export async function removeAutomationRule(
  ruleId: number,
  projectId: number
): Promise<void> {
  await apiDeleteAutomationRule(ruleId);
  const cur = automationRulesSignal.value;
  if (cur.projectId === projectId) {
    automationRulesSignal.value = {
      ...cur,
      items: cur.items.filter((r) => r.id !== ruleId),
    };
  }
}
